import React, { useEffect, useMemo } from "react";
import { ActivitySquare, AlertTriangle, ArrowRightLeft, CheckCircle2, CircleOff, FileText, Network, ShieldAlert, UserRound, X } from "lucide-react";
import { parseConfigActionNote } from "./configActionNoteParser";
import { formatMlDetectionDate } from "../utils";

function getLabelClass(result) {
    const value = String(result || "").toLowerCase();
    if (value === "suspicious" || value === "anomaly" || value === "high_risk") {
        return "oct-config-impact-popup__status oct-config-impact-popup__status--risk";
    }
    return "oct-config-impact-popup__status oct-config-impact-popup__status--normal";
}

function renderChip(text, className = "") {
    return (
        <span key={text} className={`oct-config-impact-popup__chip ${className}`.trim()}>
            {text}
        </span>
    );
}

function buildDisplayModelNote(summary, result) {
    const payload = {
        label: result || null,
        risk_score: summary.modelRiskScore ? Number(summary.modelRiskScore) : null,
        confidence: summary.modelConfidence ? Number(summary.modelConfidence) : null,
        action_category: summary.actionCategory || null,
        reason: summary.modelReason || null,
        recommended_action: summary.modelRecommendedAction || null,
    };

    const filteredPayload = Object.fromEntries(
        Object.entries(payload).filter(([, value]) => value !== null && value !== "")
    );

    return Object.keys(filteredPayload).length
        ? JSON.stringify(filteredPayload, null, 2)
        : "";
}

function OverviewCard({ icon, label, value }) {
    return (
        <div className="oct-config-impact-popup__overview-card">
            <div className="oct-config-impact-popup__overview-icon">{icon}</div>
            <div>
                <span className="oct-config-impact-popup__overview-label">{label}</span>
                <strong className="oct-config-impact-popup__overview-value">{value || "N/A"}</strong>
            </div>
        </div>
    );
}

function DetailBlock({ title, icon, children, emptyText = "No data available" }) {
    const hasChildren = React.Children.count(children) > 0;

    return (
        <section className="oct-config-impact-popup__detail-block">
            <div className="oct-config-impact-popup__detail-head">
                {icon}
                <h4>{title}</h4>
            </div>
            <div className="oct-config-impact-popup__detail-body">
                {hasChildren ? children : <p className="oct-config-impact-popup__empty">{emptyText}</p>}
            </div>
        </section>
    );
}

export default function ConfigActionImpactPopup({ data, closePopup }) {
    const summary = useMemo(() => parseConfigActionNote(data?.note), [data?.note]);
    const formattedDate = useMemo(() => formatMlDetectionDate(data?.date), [data?.date]);
    const displayModelNote = useMemo(
        () => buildDisplayModelNote(summary, data?.detectionResults),
        [summary, data?.detectionResults]
    );

    useEffect(() => {
        document.body.style.overflow = "hidden";

        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                closePopup();
            }
        };

        document.addEventListener("keydown", onKeyDown);

        return () => {
            document.body.style.overflow = "";
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [closePopup]);

    return (
        <div className="oct-config-impact-popup" role="dialog" aria-modal="true" aria-labelledby="oct-config-impact-title">
            <div className="oct-config-impact-popup__backdrop" onClick={closePopup} />

            <div className="oct-config-impact-popup__dialog" onClick={(event) => event.stopPropagation()}>
                <div className="oct-config-impact-popup__header">
                    <div className="oct-config-impact-popup__header-copy">
                        <div className="oct-config-impact-popup__icon">
                            <ShieldAlert size={24} />
                        </div>
                        <div>
                            <p className="oct-config-impact-popup__eyebrow">Config action analysis</p>
                            <h3 id="oct-config-impact-title">Details & impact review</h3>
                            <p className="oct-config-impact-popup__subtext">
                                Full offline Ollama decision context, config diff indicators, and validation correlation in one place.
                            </p>
                        </div>
                    </div>

                    <div className="oct-config-impact-popup__header-actions">
                        <span className={getLabelClass(data?.detectionResults)}>{data?.detectionResults || "normal"}</span>
                        <button
                            type="button"
                            className="oct-config-impact-popup__close"
                            onClick={closePopup}
                            aria-label="Close config action details popup"
                        >
                            <X size={18} />
                        </button>
                    </div>
                </div>

                <div className="oct-config-impact-popup__body">
                    <div className="oct-config-impact-popup__overview-grid">
                        <OverviewCard icon={<UserRound size={18} />} label="User" value={data?.usr} />
                        <OverviewCard icon={<Network size={18} />} label="Source IP" value={data?.ip} />
                        <OverviewCard icon={<FileText size={18} />} label="Snapshot" value={summary.configFileName} />
                        <OverviewCard icon={<ActivitySquare size={18} />} label="Vendor" value={summary.vendor} />
                    </div>

                    <div className="oct-config-impact-popup__meta-row">
                        {renderChip(`Date ${formattedDate.displayDateTime || 'N/A'}`)}
                        {summary.configChangePk ? renderChip(`Change #${summary.configChangePk}`, "is-accent") : null}
                        {summary.addedCount !== null ? renderChip(`Added ${summary.addedCount}`) : null}
                        {summary.removedCount !== null ? renderChip(`Removed ${summary.removedCount}`) : null}
                        {summary.validationResults ? renderChip(`Validation ${summary.validationResults}`, "is-soft") : null}
                        {summary.recentViolations ? renderChip(`Recent violations ${summary.recentViolations}`, "is-warning") : null}
                        {summary.configSnapshots ? renderChip(`Snapshots ${summary.configSnapshots}`, "is-soft") : null}
                    </div>

                    <div className="oct-config-impact-popup__content-grid">
                        <div className="oct-config-impact-popup__column">
                            <DetailBlock title="Risk correlation" icon={<AlertTriangle size={18} />}>
                                {summary.modelRiskScore || summary.modelConfidence ? (
                                    <div className="oct-config-impact-popup__chip-wrap">
                                        {summary.modelRiskScore ? renderChip(`Risk ${summary.modelRiskScore}/100`, "is-warning") : null}
                                        {summary.modelConfidence ? renderChip(`Confidence ${summary.modelConfidence}/100`, "is-soft") : null}
                                        {summary.actionCategory ? renderChip(summary.actionCategory, "is-accent") : null}
                                    </div>
                                ) : null}

                                {summary.transitions?.length ? (
                                    <div className="oct-config-impact-popup__chip-wrap">
                                        {summary.transitions.map((item) => renderChip(item, "is-warning"))}
                                    </div>
                                ) : null}

                                {summary.severity?.length ? (
                                    <p className="oct-config-impact-popup__line">
                                        <strong>Severity impact:</strong> {summary.severity.join(", ")}
                                    </p>
                                ) : null}

                                {summary.ruleContext ? (
                                    <p className="oct-config-impact-popup__line">
                                        <strong>Rule context:</strong> {summary.ruleContext}
                                    </p>
                                ) : null}

                                {summary.msbContext ? (
                                    <p className="oct-config-impact-popup__line">
                                        <strong>MSB context:</strong> {summary.msbContext}
                                    </p>
                                ) : null}

                                {summary.explicitValidationResults ? (
                                    <p className="oct-config-impact-popup__line">
                                        <strong>Validation references:</strong> {summary.explicitValidationResults}
                                    </p>
                                ) : null}
                            </DetailBlock>

                            <DetailBlock title="Rules & baselines" icon={<CheckCircle2 size={18} />}>
                                {summary.rules?.length ? (
                                    <div className="oct-config-impact-popup__list-group">
                                        <span className="oct-config-impact-popup__list-label">Rules</span>
                                        <ul>
                                            {summary.rules.map((item) => <li key={item}>{item}</li>)}
                                        </ul>
                                    </div>
                                ) : null}

                                {summary.msbs?.length ? (
                                    <div className="oct-config-impact-popup__list-group">
                                        <span className="oct-config-impact-popup__list-label">MSBs</span>
                                        <ul>
                                            {summary.msbs.map((item) => <li key={item}>{item}</li>)}
                                        </ul>
                                    </div>
                                ) : null}
                            </DetailBlock>
                        </div>

                        <div className="oct-config-impact-popup__column">
                            <DetailBlock title="Configuration change view" icon={<ArrowRightLeft size={18} />}>
                                {summary.changedSections?.length ? (
                                    <div className="oct-config-impact-popup__list-group">
                                        <span className="oct-config-impact-popup__list-label">Changed sections</span>
                                        <div className="oct-config-impact-popup__chip-wrap">
                                            {summary.changedSections.map((item) => renderChip(item))}
                                        </div>
                                    </div>
                                ) : null}

                                {summary.diffSnippet ? (
                                    <div className="oct-config-impact-popup__diff-box">
                                        <span className="oct-config-impact-popup__list-label">Diff signal</span>
                                        <p>{summary.diffSnippet}</p>
                                    </div>
                                ) : null}
                            </DetailBlock>

                            <DetailBlock title="Model output summary" icon={<CircleOff size={18} />}>
                                {summary.modelReason ? (
                                    <div className="oct-config-impact-popup__diff-box">
                                        <span className="oct-config-impact-popup__list-label">Why Ollama flagged this</span>
                                        <p>{summary.modelReason}</p>
                                    </div>
                                ) : null}
                                {summary.modelRecommendedAction ? (
                                    <div className="oct-config-impact-popup__diff-box">
                                        <span className="oct-config-impact-popup__list-label">Recommended action</span>
                                        <p>{summary.modelRecommendedAction}</p>
                                    </div>
                                ) : null}
                                {displayModelNote ? (
                                    <div className="oct-config-impact-popup__diff-box">
                                        <span className="oct-config-impact-popup__list-label">AI summary payload</span>
                                        <pre className="oct-config-impact-popup__code-block">
                                            {displayModelNote}
                                        </pre>
                                    </div>
                                ) : null}
                            </DetailBlock>
                        </div>
                    </div>
                </div>

                <div className="oct-config-impact-popup__footer">
                    <button type="button" className="btn oct-config-impact-popup__btn oct-config-impact-popup__btn--secondary" onClick={closePopup}>
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}
